import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";

const SECTIONS = [
  { id: "editeur", n: "1", t: "Identification de l'éditeur" },
  { id: "hebergement", n: "2", t: "Hébergement" },
  { id: "structure", n: "3", t: "Structure d'investissement — Constellation de SPV" },
  { id: "risques", n: "4", t: "Avertissements et risques financiers" },
  { id: "amf", n: "5", t: "Statut réglementaire vis-à-vis de l'AMF" },
  { id: "ip", n: "6", t: "Propriété intellectuelle" },
  { id: "responsabilite", n: "7", t: "Limitation de responsabilité" },
  { id: "marketplace", n: "8", t: "Marketplace secondaire" },
  { id: "droit", n: "9", t: "Droit applicable et compétence juridictionnelle" },
  { id: "lcbft", n: "10", t: "Lutte contre le blanchiment (LCB-FT)" },
  { id: "cgu", n: "11", t: "Conditions générales de vente / d'utilisation" },
  { id: "qualite", n: "12", t: "Qualité de l'investisseur" },
  { id: "maj", n: "13", t: "Mises à jour" },
];

export default function Legal() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0.01 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div>
      {/* Header */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
          <div className="label-divider mb-6">{lang === "fr" ? "Partie I" : "Part I"} · {t.footer.legal}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900" data-testid="legal-title">
            Mentions légales
          </h1>
          <p className="mt-4 text-sm text-slate-500 font-mono">En vigueur au : 6 mai 2026</p>
          {lang === "en" && (
            <p className="mt-4 text-xs text-slate-500 max-w-2xl">
              The following legal notices are intentionally provided in their authoritative French version.
            </p>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24" data-testid="legal-toc">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Sommaire</div>
              <ol className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`block text-sm leading-snug py-1.5 pl-3 border-l-2 transition-colors ${
                        active === s.id
                          ? "border-slate-900 text-slate-900 font-semibold"
                          : "border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400"
                      }`}
                      data-testid={`toc-${s.id}`}
                    >
                      <span className="font-mono text-[11px] mr-2">{s.n}.</span>{s.t}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 space-y-14">
            <Section id="editeur" n="1" title="Identification de l'éditeur">
              <p>
                Le site internet accessible à l'adresse{" "}
                <a href="https://invest-gpu.com" className="link-underline text-slate-900 font-medium" target="_blank" rel="noopener noreferrer">
                  https://invest-gpu.com
                </a>{" "}
                (ci-après le <em>« Site »</em>) est édité par la société{" "}
                <strong className="text-slate-900">BUSINESS GPU LTD</strong>, Company number 16264145, dont le siège social est situé{" "}
                <strong className="text-slate-900">2 Flinders Drive, Peterborough, Cambridgeshire, United Kingdom, PE7 0NB</strong>.
              </p>
              <KV
                rows={[
                  ["Email éditeur", <a href="mailto:contact@invest-gpu.com" className="link-underline" key="e">contact@invest-gpu.com</a>],
                  ["Directeur de la publication", "Mark Wallace"],
                  ["Numéro d'enregistrement", "16264145"],
                ]}
              />
            </Section>

            <Section id="hebergement" n="2" title="Hébergement">
              <p>Le site est hébergé par <strong className="text-slate-900">Hostinger</strong>.</p>
              <KV rows={[["Contact hébergeur", <a href="mailto:contact@hotsinger.com" className="link-underline" key="h">contact@hotsinger.com</a>]]} />
            </Section>

            <Section id="structure" n="3" title="Structure d'investissement — Constellation de SPV">
              <SubH>3.1 Présentation du modèle</SubH>
              <p>
                InvestGPU met des investisseurs en relation avec des Sociétés à Responsabilité Limitée (SPV) spécialement
                constituées pour la détention d'actifs matériels (cartes graphiques GPU) destinés à la location de puissance
                de calcul.
              </p>
              <p>
                Chaque SPV est une entité juridique distincte, qui acquiert, détient et exploite un parc de matériel GPU en
                le mettant à disposition de clients professionnels.
              </p>
              <p>
                La liste des SPV associés au projet InvestGPU est disponible dans l'espace investisseur après validation du
                dossier de qualification.
              </p>

              <SubH>3.2 Rôle d'InvestGPU</SubH>
              <p>InvestGPU intervient en qualité :</p>
              <BulletList items={[
                "de plateforme d'information et de mise en relation entre les investisseurs et les SPV ;",
                "de prestataire de services de présentation d'offres d'investissement, sans intermédiation en opérations bancaires ou en services d'investissement (pas de PSI, pas de CIF) ;",
                "d'intermédiaire introduisant les investisseurs auprès des SPV dont elle détient une participation directe ou indirecte.",
              ]} />
            </Section>

            <Section id="risques" n="4" title="Avertissements et risques financiers">
              <SubH>4.1 Avertissement général</SubH>
              <p>Les investissements effectués par l'intermédiaire d'InvestGPU présentent les risques suivants :</p>
              <RiskGrid items={[
                {
                  k: "a",
                  t: "Risque de perte en capital",
                  d: "La valeur de la participation à un SPV peut diminuer et l'investisseur peut perdre tout ou partie du capital investi. Le capital investi n'est pas garanti.",
                },
                {
                  k: "b",
                  t: "Risque d'illiquidité",
                  d: "Les parts de SPV ne bénéficient d'aucun marché secondaire réglementé. Leur revente est soumise à la disponibilité d'un acheteur et à l'accord préalable de la société éditrice. L'investisseur doit considérer son investissement comme bloqué pour une durée indicative de trois (3) ans.",
                },
                {
                  k: "c",
                  t: "Risque de rendement non garanti",
                  d: "Les projections de rendement affichées sur le Site constituent des simulations sur la base d'hypothèses définies par la société et ne constituent en aucun cas un engagement contractuel, une garantie ou une promesse de résultat. Les performances passées ou simulées ne préjugent en rien des performances futures.",
                },
                {
                  k: "d",
                  t: "Risque opérationnel",
                  d: "Le modèle économique repose sur la capacité des SPV à maintenir un taux d'occupation élevé de leur parc GPU, sur la pérennité des contrats de location conclus avec les clients, et sur la capacité à renouveler le matériel dans des délais et à des coûts compatibles avec les projections financières. Tout écart sur ces paramètres est susceptible d'impacter le rendement effectif.",
                },
                {
                  k: "e",
                  t: "Risque réglementaire",
                  d: "L'activité d'InvestGPU et des SPV est susceptible d'être soumise à un cadre réglementaire évolutif, notamment en matière d'offre au public de titres financiers, de lutte contre le blanchiment des capitaux et le financement du terrorisme (LCB-FT), et de protection des données personnelles (RGPD). Aucun changement réglementaire ne garantit à l'investisseur un droit à la restitution du capital.",
                },
                {
                  k: "f",
                  t: "Risque de dépendance technologique",
                  d: "L'activité des SPV est exposée aux évolutions technologiques rapides du secteur des GPU. L'obsolescence accélérée du matériel peut nécessiter des investissements de renouvellement supérieurs aux projections initiales.",
                },
              ]} />

              <SubH>4.2 Information sur l'éligibilité des offres</SubH>
              <p>
                InvestGPU se réserve le droit de soumettre toute offre d'investissement à un processus de qualification
                préalable de l'investisseur, incluant la vérification de son profil, de sa capacité à supporter les risques
                financiers, et de sa conformité aux réglementations en vigueur.
              </p>
              <p>
                L'accès à certains investissements peut être limité aux États membres de l'Union européenne ou à d'autres
                juridictions agréées par InvestGPU.
              </p>
            </Section>

            <Section id="amf" n="5" title="Statut réglementaire vis-à-vis de l'AMF">
              <p>
                La société éditrice <strong className="text-slate-900">ne détient pas d'agrément</strong> en qualité de
                Prestataire de Services d'Investissement (PSI), de Conseiller en Investissements Financiers (CIF) ni de
                Conseiller en Investissements Participatifs (CIP) au sens du Code monétaire et financier.
              </p>
              <p>
                Elle n'exerce pas d'activité de réception et transmission d'ordres, de gestion de portefeuille, ni de
                placement garanti.
              </p>
              <p>
                En conséquence, InvestGPU ne fournit pas de conseil en investissement personnalisé. Les informations
                figurant sur le Site ont un caractère général et informatif. Elles ne constituent ni une recommandation
                personnalisée, ni une incitation à investir.
              </p>
              <p>
                La société se réserve le droit d'évoluer vers un statut réglementé si la nature, le volume ou la structure de
                ses offres d'investissement l'exige.
              </p>
            </Section>

            <Section id="ip" n="6" title="Propriété intellectuelle">
              <p>
                L'ensemble du contenu présent sur le Site — textes, images, graphismes, logos, icônes, vidéos, animations
                sonores, logiciels, code source, architecture du site, simulateurs — est la propriété exclusive de la société
                éditrice ou de ses partenaires et est protégé par les lois françaises et internationales relatives à la
                propriété intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication, adaptation, traduction (même partielle), quel
                que soit le moyen ou le procédé utilisé, est interdite sans l'accord écrit préalable de la société éditrice.
              </p>
              <p>
                Toute exploitation non autorisée du Site ou de l'un quelconque des éléments qu'il contient sera considérée
                comme constitutive d'une contrefaçon et poursuivie conformément aux dispositions des articles{" "}
                <span className="font-mono text-slate-700">L. 335-2</span> et suivants du Code de la propriété intellectuelle.
              </p>
            </Section>

            <Section id="responsabilite" n="7" title="Limitation de responsabilité">
              <SubH>7.1 Informations et projections</SubH>
              <p>
                Les informations contenues sur le Site sont fournies à titre indicatif. InvestGPU s'efforce de mettre à
                jour et de corriger les informations mises à disposition. Toutefois, elle ne peut garantir l'exactitude, la
                précision ou l'exhaustivité des informations disponibles, notamment des projections de rendement, des
                simulations et des données de marché.
              </p>
              <SubH>7.2 Disponibilité du service</SubH>
              <p>
                La société ne garantit pas que le Site fonctionnera de manière ininterrompue ou exempte d'erreur. Elle ne
                saurait être tenue responsable des dommages résultant de l'impossibilité d'accéder au Site, de l'interruption
                du service, de la modification ou de la suppression de tout service proposé.
              </p>
              <SubH>7.3 Investissements et décisions</SubH>
              <p>
                InvestGPU décline toute responsabilité quant aux décisions d'investissement prises par les utilisateurs sur
                la base des informations figurant sur le Site. L'utilisateur assume seul la responsabilité de ses décisions
                d'investissement et s'engage à ne pas engager la responsabilité civile ou pénale de la société éditrice.
              </p>
              <SubH>7.4 Liens hypertextes</SubH>
              <p>
                Le Site peut contenir des liens hypertextes vers d'autres sites internet. InvestGPU ne dispose d'aucun
                contrôle sur le contenu de ces sites tiers et décline toute responsabilité quant à leur contenu, leur
                disponibilité ou les pratiques en matière de protection des données.
              </p>
            </Section>

            <Section id="marketplace" n="8" title="Marketplace secondaire">
              <SubH>8.1 Présentation</SubH>
              <p>
                InvestGPU met à la disposition des investisseurs ayant souscrit à un SPV un service de mise en relation
                leur permettant d'exprimer leur intention de céder tout ou partie de leur participation, sous réserve des
                conditions prévues par les statuts du SPV concerné et l'accord préalable de la société.
              </p>

              <SubH>8.2 Cadre juridique de la cession</SubH>
              <p>Les cessions de parts de SPV opérées par l'intermédiaire du Site sont soumises :</p>
              <BulletList items={[
                "à la conformité avec les statuts du SPV concerné, notamment en matière de clauses d'agrément ou de préemption ;",
                "à l'acceptation écrite de la société éditrice ;",
                "au respect des dispositions légales applicables aux cessions de parts sociales ;",
                "à l'achèvement des formalités de déclaration et d'enregistrement auprès des administrations compétentes.",
              ]} />
              <p>InvestGPU ne garantit ni la réalisation des cessions, ni le prix de cession, ni les délais de traitement des demandes.</p>

              <SubH>8.3 Traitement des demandes</SubH>
              <p>
                Les demandes de cession sont traitées selon le principe du{" "}
                <em>premier arrivé, premier servi</em>, sous réserve de la compatibilité avec les statuts du SPV et de la
                validation par la société.
              </p>
              <p>Toute cession effective donne lieu à la mise à jour du registre des associés du SPV concerné.</p>
            </Section>

            <Section id="droit" n="9" title="Droit applicable et compétence juridictionnelle">
              <p>
                Les présentes mentions légales, l'utilisation du Site et tout investissement effectué par l'intermédiaire
                d'InvestGPU sont régis par <strong className="text-slate-900">le droit britannique</strong>.
              </p>
              <p>
                En cas de litige relatif à l'interprétation ou à l'exécution des présentes, les{" "}
                <strong className="text-slate-900">tribunaux britanniques sont seuls compétents</strong>, nonobstant appel en
                garantie ou pluralité de défendeurs, sauf dispositions d'ordre public contraires applicables aux
                consommateurs.
              </p>
            </Section>

            <Section id="lcbft" n="10" title="Lutte contre le blanchiment et le financement du terrorisme (LCB-FT)">
              <p>
                Dans le cadre de ses activités, InvestGPU s'engage à respecter les obligations découlant de la législation
                britannique et européenne en matière de lutte contre le blanchiment des capitaux et le financement du
                terrorisme (LCB-FT), notamment :
              </p>
              <BulletList items={[
                "La vérification de l'identité de ses investisseurs (KYC — Know Your Customer) ;",
                "La vérification de l'origine des fonds investis ;",
                "Le signalement des opérations suspectes aux autorités compétentes ;",
                "La conservation des documents relatifs à ces vérifications pendant cinq (5) ans à compter de la fin de la relation d'affaires.",
              ]} />
              <p>
                Les investisseurs s'engagent à fournir des informations exactes, complètes et à jour. Toute information
                fausse ou incomplète peut entraîner le refus de suivi de la demande d'investissement ou la résiliation de la
                relation contractuelle.
              </p>
            </Section>

            <Section id="cgu" n="11" title="Conditions générales de vente / d'utilisation">
              <p>
                Les investisseurs sont invités à consulter les{" "}
                <strong className="text-slate-900">Conditions Générales d'Utilisation (CGU)</strong> et les{" "}
                <strong className="text-slate-900">Conditions Générales d'Investissement (CGI)</strong> disponibles dans
                l'espace investisseur et dans la data room avant toute souscription.
              </p>
              <p>
                La prise de connaissance, l'acceptation et la signature électronique des documents contractuels constituent
                un préalable obligatoire à toute souscription.
              </p>
            </Section>

            <Section id="qualite" n="12" title="Qualité de l'investisseur">
              <p>En souscrivant à un investissement par l'intermédiaire d'InvestGPU, l'investisseur reconnaît :</p>
              <BulletList items={[
                "Avoir lu et compris les présentes mentions légales et les risques associés ;",
                "Disposer d'une situation financière lui permettant de supporter une perte partielle ou totale de son investissement ;",
                "Effectuer cet investissement dans le cadre de la gestion de son patrimoine personnel ;",
                "Ne pas se trouver dans un cas d'interdiction légale ou réglementaire d'exercer l'activité d'investisseur.",
              ]} />
            </Section>

            <Section id="maj" n="13" title="Mises à jour">
              <p>
                La société se réserve le droit de modifier les présentes mentions légales à tout moment. Les modifications
                sont portées à la connaissance des investisseurs par affichage sur le Site. Il appartient aux investisseurs
                de consulter régulièrement les mentions légales.
              </p>
            </Section>

            <div className="border-t border-slate-200 pt-8 text-xs text-slate-500">
              <p className="font-mono">— Fin de la Partie I —</p>
              <p className="mt-3 leading-relaxed">{t.footer.disclaimer}</p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

function Section({ id, n, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`legal-section-${id}`}>
      <div className="flex items-baseline gap-4 mb-5">
        <span className="font-mono text-xs text-emerald-700 font-bold tracking-wider">{n}.</span>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
      </div>
      <div className="space-y-4 text-slate-700 leading-relaxed text-[15px] max-w-3xl">{children}</div>
    </section>
  );
}

function SubH({ children }) {
  return <h3 className="font-display text-base font-semibold text-slate-900 mt-6 mb-2 uppercase tracking-[0.05em]">{children}</h3>;
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 pl-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="text-emerald-700 mt-1.5 shrink-0 inline-block w-1 h-1 bg-emerald-700" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function KV({ rows }) {
  return (
    <table className="w-full max-w-2xl mt-3 border border-slate-200 text-sm">
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} className={i > 0 ? "border-t border-slate-200" : ""}>
            <td className="py-2.5 px-4 text-slate-500 bg-slate-50 w-56">{k}</td>
            <td className="py-2.5 px-4 text-slate-900 font-medium">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RiskGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-4">
      {items.map((r) => (
        <div key={r.k} className="border border-slate-200 bg-white p-5 rounded-sm">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-mono text-[11px] text-emerald-700 font-bold uppercase">{r.k})</span>
            <h4 className="font-display font-semibold text-slate-900 text-base">{r.t}</h4>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">{r.d}</p>
        </div>
      ))}
    </div>
  );
}
